import type { LinhaHierarquia, NivelAnuncio } from '@baiocchi/shared'
import {
  Botao,
  Cartao,
  Dica,
  Etiqueta,
  EstadoVazio,
  TabelaDados,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useQueries } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import {
  AvisoDefasagem,
  BadgeFadiga,
  BadgeScore,
  SetaTendencia,
  formatarMetrica,
} from './componentes'
import { FiltrosMetaAds, parametrosDoFiltro } from './filtros'

/**
 * TELA-META-03/04/05 — Campanha → Conjunto → Anúncio.
 *
 * ## Uma tela para os três níveis
 *
 * A navegação é `campanhaId` e `conjuntoId` na URL. Sem eles, a tela lista campanhas; com
 * `campanhaId`, os conjuntos dela; com `conjuntoId`, os anúncios. Três telas quase idênticas
 * divergiriam na primeira coluna nova — e a URL continua sendo um link compartilhável para
 * o nível exato que o gestor está olhando.
 *
 * ## As colunas foram escolhidas, não acumuladas
 *
 * O §26 pede para não ser "uma tela genérica cheia de tabelas". A tabela aqui existe porque
 * comparar 20 campanhas em cards é pior, mas ela mostra **o que muda decisão**: nome,
 * status, orçamento, investimento, leads, CPL, score, tendência, fadiga e o resultado no
 * CRM. CPM, alcance e impressões ficam na visão geral e no detalhe — na comparação lado a
 * lado eles só ocupam largura.
 */
export function PaginaMetaAdsHierarquia() {
  const { filtros, definir } = useFiltrosUrl()
  const parametros = {
    ...parametrosDoFiltro(filtros),
    campanhaId: filtros.campanhaId,
    conjuntoId: filtros.conjuntoId,
    apenasAtivos: filtros.apenasAtivos,
  }

  const [contas, consulta] = useQueries({
    queries: [
      {
        queryKey: chaves.metaAds.contas,
        queryFn: ({ signal }: { signal: AbortSignal }) => metaAdsApi.contas(signal),
        staleTime: 300_000,
      },
      {
        queryKey: chaves.metaAds.hierarquia(parametros),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          metaAdsApi.hierarquia(parametros, signal),
        staleTime: 60_000,
      },
    ],
  })

  const dados = consulta.data?.dados
  const nivel = dados?.nivel ?? 'CAMPANHA'

  return (
    <>
      <CabecalhoPagina
        titulo={TITULO_POR_NIVEL[nivel]}
        descricao={DESCRICAO_POR_NIVEL[nivel]}
        acoes={
          <label className="flex items-center gap-2 text-sm text-conteudo-suave">
            <input
              type="checkbox"
              checked={filtros.apenasAtivos === 'true'}
              onChange={(evento) =>
                definir({ apenasAtivos: evento.target.checked ? 'true' : null })
              }
              className="h-4 w-4 rounded border-borda"
            />
            Só ativos
          </label>
        }
      />

      <Trilha definir={definir} nivel={nivel} />

      <FiltrosMetaAds
        filtros={filtros}
        definir={definir}
        contas={contas.data?.dados ?? []}
        carregandoContas={contas.isLoading}
      />

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}

      <Cartao semPadding>
        <TabelaDados<LinhaHierarquia>
          colunas={colunasDe(nivel, definir)}
          itens={dados?.linhas ?? []}
          chaveDe={(linha) => linha.idExterno}
          carregando={consulta.isLoading}
          vazio={
            <EstadoVazio
              titulo="Nada sincronizado neste recorte"
              descricao="Ajuste o período ou rode a sincronização de estrutura em Contas."
            />
          }
          cartaoMobile={(linha) => <CartaoLinha linha={linha} nivel={nivel} definir={definir} />}
          rodape={
            dados === undefined ? null : (
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                <span className="text-conteudo-suave">
                  {dados.linhas.length} {nivel === 'CAMPANHA' ? 'campanha(s)' : nivel === 'CONJUNTO' ? 'conjunto(s)' : 'anúncio(s)'}
                </span>
                <span className="flex flex-wrap gap-x-4 gap-y-1 tabular-nums text-conteudo">
                  <span>Total {formatarMetrica(dados.totais.gasto, 'MOEDA')}</span>
                  <span>{dados.totais.leads} leads</span>
                  <span>CPL {formatarMetrica(dados.totais.cpl, 'MOEDA')}</span>
                </span>
              </div>
            )
          }
        />
      </Cartao>

      <div className="mt-4">
        <AvisoDefasagem
          defasagemMinutos={consulta.data?.meta.defasagemMinutos ?? 0}
          atualizadoEm={consulta.data?.meta.atualizadoEm}
        />
      </div>
    </>
  )
}

const TITULO_POR_NIVEL: Readonly<Record<NivelAnuncio, string>> = {
  CONTA: 'Contas',
  CAMPANHA: 'Campanhas',
  CONJUNTO: 'Conjuntos',
  ANUNCIO: 'Anúncios',
}

const DESCRICAO_POR_NIVEL: Readonly<Record<NivelAnuncio, string>> = {
  CONTA: 'Visão por conta de anúncio.',
  CAMPANHA: 'Clique em uma campanha para ver os conjuntos dela.',
  CONJUNTO: 'Clique em um conjunto para ver os anúncios dele.',
  ANUNCIO: 'Anúncios do conjunto, com criativo e score.',
}

/** Migalha de navegação da árvore. Permite subir um nível sem perder conta e período. */
function Trilha({
  definir,
  nivel,
}: {
  readonly definir: (novos: Record<string, string | null>) => void
  readonly nivel: NivelAnuncio
}) {
  if (nivel === 'CAMPANHA') return null

  return (
    <nav className="mb-3 flex items-center gap-2 text-sm" aria-label="Navegação da hierarquia">
      <Botao
        variante="fantasma"
        tamanho="sm"
        onClick={() => definir({ campanhaId: null, conjuntoId: null })}
      >
        Campanhas
      </Botao>
      {nivel === 'ANUNCIO' ? (
        <>
          <span className="text-conteudo-fraco">/</span>
          <Botao variante="fantasma" tamanho="sm" onClick={() => definir({ conjuntoId: null })}>
            Conjuntos
          </Botao>
        </>
      ) : null}
      <span className="text-conteudo-fraco">/</span>
      <span className="text-conteudo-suave">{TITULO_POR_NIVEL[nivel]}</span>
    </nav>
  )
}

function colunasDe(
  nivel: NivelAnuncio,
  definir: (novos: Record<string, string | null>) => void,
): ReadonlyArray<ColunaTabela<LinhaHierarquia>> {
  const podeDescer = nivel === 'CAMPANHA' || nivel === 'CONJUNTO'

  return [
    {
      chave: 'nome',
      cabecalho: 'Nome',
      classe: 'min-w-[16rem]',
      celula: (linha) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {podeDescer && linha.totalFilhos > 0 ? (
              <button
                type="button"
                className="truncate text-left font-medium text-marca-700 hover:underline"
                onClick={() =>
                  definir(
                    nivel === 'CAMPANHA'
                      ? { campanhaId: linha.idExterno, conjuntoId: null }
                      : { conjuntoId: linha.idExterno },
                  )
                }
              >
                {linha.nome}
              </button>
            ) : (
              <span className="truncate font-medium text-conteudo">{linha.nome}</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <EtiquetaStatusMeta linha={linha} />
            {podeDescer && linha.totalFilhos > 0 ? (
              <span className="text-xs text-conteudo-fraco">
                {linha.totalFilhos} {nivel === 'CAMPANHA' ? 'conjuntos' : 'anúncios'}
              </span>
            ) : null}
            {linha.objetivo !== null ? (
              <span className="text-xs text-conteudo-fraco">{linha.objetivo}</span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      chave: 'orcamento',
      cabecalho: 'Orçamento/dia',
      alinhamento: 'direita',
      ocultarAte: 'lg',
      celula: (linha) =>
        linha.orcamento.diario === null ? (
          <Dica texto="Orçamento não definido neste nível — pode estar na campanha (CBO) ou já esgotado.">
            <span className="text-conteudo-fraco">—</span>
          </Dica>
        ) : (
          <span className="tabular-nums">
            {formatarMetrica(linha.orcamento.diario, 'MOEDA')}
          </span>
        ),
    },
    {
      chave: 'gasto',
      cabecalho: 'Investimento',
      alinhamento: 'direita',
      celula: (linha) => (
        <span className="tabular-nums">{formatarMetrica(linha.metricas.gasto, 'MOEDA')}</span>
      ),
    },
    {
      chave: 'leads',
      cabecalho: 'Leads',
      alinhamento: 'direita',
      celula: (linha) => (
        <span className="tabular-nums">{formatarMetrica(linha.metricas.leads, 'INTEIRO')}</span>
      ),
    },
    {
      chave: 'cpl',
      cabecalho: 'CPL',
      alinhamento: 'direita',
      celula: (linha) => (
        <span className="tabular-nums">{formatarMetrica(linha.metricas.cpl, 'MOEDA')}</span>
      ),
    },
    {
      chave: 'ctr',
      cabecalho: 'CTR',
      alinhamento: 'direita',
      ocultarAte: 'md',
      celula: (linha) => (
        <span className="tabular-nums">{formatarMetrica(linha.metricas.ctr, 'PERCENTUAL')}</span>
      ),
    },
    {
      chave: 'crm',
      cabecalho: 'No CRM',
      alinhamento: 'direita',
      ocultarAte: 'xl',
      celula: (linha) =>
        linha.crm === null ? (
          <Dica texto="Nenhum lead deste alvo foi atribuído no CRM neste período. Leads antigos não trazem os IDs de anúncio.">
            <span className="text-conteudo-fraco">—</span>
          </Dica>
        ) : (
          <Dica
            texto={
              `${linha.crm.leadsNoCrm} leads no CRM · ${linha.crm.emAtendimento} em atendimento · ` +
              `${linha.crm.vendas} venda(s) · ${linha.crm.perdidos} perdido(s)` +
              (linha.crm.custoPorVenda === null
                ? ''
                : ` · custo por venda ${formatarMetrica(linha.crm.custoPorVenda, 'MOEDA')}`)
            }
          >
            <span className="tabular-nums">
              {linha.crm.leadsNoCrm}
              <span className="text-conteudo-fraco"> / </span>
              <span className="text-sucesso">{linha.crm.vendas}</span>
            </span>
          </Dica>
        ),
    },
    {
      chave: 'tendencia',
      cabecalho: 'Tendência',
      ocultarAte: 'lg',
      celula: (linha) => <SetaTendencia tendencia={linha.tendenciaCpl} />,
    },
    {
      chave: 'fadiga',
      cabecalho: 'Criativo',
      ocultarAte: 'lg',
      celula: (linha) => <BadgeFadiga fadiga={linha.fadiga} />,
    },
    {
      chave: 'score',
      cabecalho: 'Score',
      alinhamento: 'direita',
      celula: (linha) => <BadgeScore score={linha.score} />,
    },
  ]
}

/**
 * Status normalizado, com o valor cru da Meta na dica.
 *
 * O valor cru importa: `PAUSADO` pode ser pausa própria ou herdada (`CAMPAIGN_PAUSED`), e a
 * diferença é onde o gestor precisa clicar para reativar.
 */
function EtiquetaStatusMeta({ linha }: { readonly linha: LinhaHierarquia }) {
  const tom =
    linha.status === 'ATIVO'
      ? 'sucesso'
      : linha.status === 'REPROVADO'
        ? 'perigo'
        : linha.status === 'EM_ANALISE'
          ? 'atencao'
          : 'neutro'

  const etiqueta = <Etiqueta tom={tom} tamanho="sm">{ROTULO_STATUS[linha.status]}</Etiqueta>

  return linha.statusExterno === null ? (
    etiqueta
  ) : (
    <Dica texto={`Status na Meta: ${linha.statusExterno}`}>{etiqueta}</Dica>
  )
}

const ROTULO_STATUS: Readonly<Record<LinhaHierarquia['status'], string>> = {
  ATIVO: 'Ativo',
  PAUSADO: 'Pausado',
  ARQUIVADO: 'Arquivado',
  EXCLUIDO: 'Excluído',
  EM_ANALISE: 'Em análise',
  REPROVADO: 'Reprovado',
  DESCONHECIDO: 'Desconhecido',
}

/** Versão em cartão para telas estreitas — exigência de responsividade de `01-screens.md`. */
function CartaoLinha({
  linha,
  nivel,
  definir,
}: {
  readonly linha: LinhaHierarquia
  readonly nivel: NivelAnuncio
  readonly definir: (novos: Record<string, string | null>) => void
}) {
  const podeDescer = (nivel === 'CAMPANHA' || nivel === 'CONJUNTO') && linha.totalFilhos > 0

  return (
    <div className="space-y-2 p-3">
      <div className="flex items-start justify-between gap-2">
        {podeDescer ? (
          <button
            type="button"
            className="min-w-0 flex-1 truncate text-left font-medium text-marca-700"
            onClick={() =>
              definir(
                nivel === 'CAMPANHA'
                  ? { campanhaId: linha.idExterno, conjuntoId: null }
                  : { conjuntoId: linha.idExterno },
              )
            }
          >
            {linha.nome}
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate font-medium text-conteudo">{linha.nome}</span>
        )}
        <BadgeScore score={linha.score} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <EtiquetaStatusMeta linha={linha} />
        <SetaTendencia tendencia={linha.tendenciaCpl} />
        <BadgeFadiga fadiga={linha.fadiga} />
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div className="flex justify-between">
          <dt className="text-conteudo-suave">Investimento</dt>
          <dd className="tabular-nums">{formatarMetrica(linha.metricas.gasto, 'MOEDA')}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-conteudo-suave">Leads</dt>
          <dd className="tabular-nums">{linha.metricas.leads}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-conteudo-suave">CPL</dt>
          <dd className="tabular-nums">{formatarMetrica(linha.metricas.cpl, 'MOEDA')}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-conteudo-suave">CTR</dt>
          <dd className="tabular-nums">{formatarMetrica(linha.metricas.ctr, 'PERCENTUAL')}</dd>
        </div>
      </dl>

      {linha.criativo !== null ? <ResumoCriativo criativo={linha.criativo} /> : null}
    </div>
  )
}

/** Copy do anúncio — o insumo do §12, visível sem abrir outra tela. */
export function ResumoCriativo({
  criativo,
}: {
  readonly criativo: NonNullable<LinhaHierarquia['criativo']>
}) {
  return (
    <div className="rounded border border-borda bg-superficie-2 p-2 text-xs">
      {criativo.titulo !== null ? (
        <p className="font-medium text-conteudo">{criativo.titulo}</p>
      ) : null}
      {criativo.corpo !== null ? (
        <p className="mt-0.5 line-clamp-3 text-conteudo-suave">{criativo.corpo}</p>
      ) : null}
      <p className="mt-1 flex flex-wrap gap-2 text-conteudo-fraco">
        <span>{criativo.tipoMidia}</span>
        {criativo.chamadaAcao !== null ? <span>{criativo.chamadaAcao}</span> : null}
      </p>
    </div>
  )
}
